<script setup lang="ts">
import {
    FREQ_AVAILABLE_BY_VARIABLES,
    VARIABLES,
} from "@modules/energy-manager/constants"
import { useVModel } from "@vueuse/core"
import { ref, computed } from "vue"
import { useI18n } from "vue-i18n"

type Props = {
    variable: string
    inputId?: string
    disabled?: boolean
}

type Item = {
    label: string
    value: string
    items?: Item[]
    command?: ({ item }: { item: Item }) => void
    key?: string
    message?: boolean
    severity?: string
}

type Variable = {
    name: string
    stat: string
    category?: string
    unit: string
}

const props = withDefaults(defineProps<Props>(), {
    inputId: "",
    disabled: false,
})
const emit = defineEmits(["update:variable"])

const menu = ref<(HTMLElement & { toggle: (event: any) => void }) | null>(null)

const modelVariable = useVModel(props, "variable", emit, {
    eventName: "update:variable",
})

function onUpdateVariable({ item }: { item: Item }) {
    modelVariable.value = item.value
}

const getCurrentValueLabel = computed(() => {
    return getVariables.value
        .flatMap((item) => item.items || [item])
        .find((item) => item.value === modelVariable.value)?.label
})

const getVariables = computed(() => {
    return (
        Object.entries(VARIABLES)
            // On ne garde que les variables avec une configuration de fréquence
            .filter(([key]) => {
                return FREQ_AVAILABLE_BY_VARIABLES.some(
                    (v) => v.variable == key
                )
            })
            .reduce((acc: Item[], item: [string, Variable]) => {
                const value = `${item[0]}.${item[1].stat}`
                const variable = item[1]

                const variableConfig = FREQ_AVAILABLE_BY_VARIABLES.find(
                    (v) => v.variable == item[0]
                )

                const itemVariable = {
                    label: `${variable.name} (${variable.unit})`,
                    key: item[0],
                    value,
                    command: onUpdateVariable,
                } as Item

                // Si le nombre de fréquence n'est pas égale à 5, alors on affiche un message d'info
                // avec les fréquences disponibles
                if (variableConfig?.frequencyConfigs?.length !== 5) {
                    itemVariable["message"] = true
                    itemVariable["severity"] = "info"
                }

                if (variable.category) {
                    const category = acc.find(
                        (i) =>
                            i.label ===
                            `${variable.category} (${variable.unit})`
                    )
                    if (category) {
                        category.items = category.items || []
                        category.items.push(itemVariable)
                    } else {
                        const categoryToAdd = {
                            label: `${variable.category} (${variable.unit})`,
                            items: [itemVariable],
                        } as Item
                        acc.push(categoryToAdd)
                    }
                } else {
                    acc.push(itemVariable)
                }
                return acc
            }, [])
            .map((item) => {
                if ("items" in item && item.items?.length === 1) {
                    return {
                        label: item.items[0].label,
                        key: item.items[0].key,
                        value: item.items[0].value,
                        command: item.items[0].command,
                        message: item.items[0].message,
                        severity: item.items[0].severity,
                    } as Item
                }
                return item
            })
    )
})

const toggle = (event: any) => {
    if (!menu.value) return
    menu.value.toggle(event)
}

const { t } = useI18n({
    messages: {
        fr: {
            info: {
                title: "Fréquence limitée pour cette variable",
                content: ({ named }: { named: (name: string) => string }) => {
                    const variable = named("variable")

                    const analyseVerb = variable.toLowerCase().includes("la")
                        ? "analysée"
                        : "analysé"

                    const sentence = `${variable} ne peut être ${analyseVerb} qu'à partir de la fréquence journalière. Les fréquences plus fines, comme l'horaire, n'ont pas de sens pour ce type d'analyse.`

                    return sentence
                },
            },
        },
    },
})
</script>

<template>
    <BaseSelect
        class="input-date-period-variable"
        placeholder="Veuillez choisir une variable"
        :default-value="modelVariable"
        :options="getVariables"
        :input-id="inputId"
        type="button"
        icon="pi pi-chevron-down"
        @click="toggle"
        aria-haspopup="true"
        aria-controls="overlay_menu"
        icon-pos="right"
        severity="secondary"
        variant="text"
        option-label="label"
        option-group-label="label"
        option-group-children="items"
        :disabled="disabled"
        :pt="{
            overlay: 'hidden',
        }"
    >
        <template #value="slotProps">
            <span
                :class="modelVariable ? 'text-primary' : ''"
                :data-empty="!modelVariable"
                >{{ getCurrentValueLabel ?? slotProps.placeholder }}</span
            >
        </template>
    </BaseSelect>
    <BaseTieredMenu ref="menu" id="overlay_menu" :model="getVariables" popup>
        <template #item="{ item, props: propsItem, hasSubmenu }">
            <a v-ripple class="variable-option-item" v-bind="propsItem.action">
                <span>{{ item.label }}</span>
                <BaseTooltipMessage
                    @click.prevent.stop
                    v-if="item.message"
                    :severity="item.severity"
                    :title="t(`${item.severity}.title`)"
                    :content="
                        t(`${item.severity}.content`, {
                            variable: t(`variables.${item.key}.sentence`),
                        })
                    "
                />
                <i v-if="hasSubmenu" class="pi pi-angle-right ml-auto"></i>
            </a>
        </template>
    </BaseTieredMenu>
</template>

<style scoped>
.input-date-period-variable {
    .input-date-period-variable__value {
        color: var(--p-primary) !important;
    }
}
.variable-option-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;

    & > span {
        white-space: nowrap;
    }
}
</style>
